import React, { useCallback, useContext, useEffect, useState } from 'react';
import type { FormikHelpers } from 'formik';
import { useFormik } from 'formik';

import { ResponseStatus } from 'app/api';
import { URL } from 'app/api/constants';
import { FormControl } from 'app/design-system/components/FormControl';
import type { CodeFormValue } from 'app/widgets/CodeConfirmation';
import {
  CODE_FORM_VALUES,
  CODE_LENGTH,
  CodeConfirmation,
  CodeError,
} from 'app/widgets/CodeConfirmation';

import { sendRequest } from '../../../actions';
import { CHECK_TIME_DELAY } from '../../../constants';
import { StepContext } from '../../../contexts/stepContext';
import { UserContext } from '../../../contexts/userContext';

import styles from './CodeForm.module.scss';

const NOT_NUMBER = /\D/g;

export const CodeForm = () => {
  const { activeStep, backwardStep, forwardStep } = useContext(StepContext);
  const { userData, setUserData } = useContext(UserContext);
  const [error, setError] = useState<CodeError | null>(userData.codeError);

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setTouched,
    setFieldValue,
    isSubmitting,
  } = useFormik({
    initialValues: CODE_FORM_VALUES,
    onSubmit: (values: CodeFormValue, formikHelpers: FormikHelpers<CodeFormValue>) => {
      const { setSubmitting } = formikHelpers;
      const { code } = values;

      if (code.length < CODE_LENGTH) {
        setError(CodeError.NOT_CODE);
        return setSubmitting(false);
      }

      setUserData((prevState) => ({ ...prevState, disabled: true }));

      sendRequest(URL.PASSWORD_RESET_CHECK_CODE, { requisite: userData.requisite, code })
        .then((res) => {
          setUserData((prevState) => ({ ...prevState, token: res.data.token, checked: true }));

          setTimeout(() => {
            setUserData((prevState) => ({ ...prevState, checked: false }));

            forwardStep(activeStep);
          }, CHECK_TIME_DELAY);
        })
        .catch(({ response }) => {
          if (response.status === ResponseStatus.UNPROCESSABLE_ENTITY) {
            response.data.type.endsWith('secure-code-token-not-found')
              ? setError(CodeError.NOT_TOKEN)
              : setError(CodeError.NOT_CODE);
          }
          else {
            setError(CodeError.SOMETHING_WRONG);
          }

          setSubmitting(false);
        })
        .finally(() => setUserData((prevState) => ({ ...prevState, disabled: false })));
    },
  });

  useEffect(() => {
    setFieldValue('code', values.code.replace(NOT_NUMBER, ''));
    if (values.code.length === CODE_LENGTH) {
      handleSubmit();
    }
    if (!!errors.code && touched.code) {
      setError(CodeError.NOT_CODE);
    }
  }, [values.code]);

  const changeVerifyDataHandler = useCallback(() => {
    backwardStep(activeStep);
  }, [backwardStep, activeStep]);

  const changeErrorState = useCallback(() => {
    setError(null);
  }, []);

  const reSendData = {
    url: URL.PASSWORD_RESET_SEND_CODE,
    data: { type: userData.type, phoneNumber: userData.requisite },
  };

  return (
    <form id={activeStep.toString()}
          onSubmit={handleSubmit}>
      <FormControl className={styles.confirmCodeForm}>
        <CodeConfirmation value={values.code}
                          data={userData}
                          changeData={setUserData}
                          reSendData={reSendData}
                          error={error}
                          changeErrorState={changeErrorState}
                          disabled={isSubmitting}
                          changeVerifyData={changeVerifyDataHandler}
                          formikHelpers={{ setFieldValue, setTouched }} />
      </FormControl>
    </form>
  );
};
